import { ReactNode } from 'react';

import { TooltipProps as MUITooltipProps } from '@mui/material/Tooltip';

import { useIsDesktop } from '../hooks';
import { Tooltip } from '../Tooltip';
import { Box } from '../Box';
import { Typography } from '../Typography';
import { Icon } from '../Icon';

/**
 * Свойства компонента {@link InputTooltip}
 */
interface InputTooltipProps {
  /**
   * Заголовок всплывающей подсказки.
   */
  title?: string;

  /**
   * Основной текст описания всплывающей подсказки.
   */
  description?: string;

  /**
   * Дополнительный текст подсказки (необязательный).
   */
  subText?: string;

  /**
   * Позиционирование всплывающей подсказки относительно целевого элемента.
   *
   * @defaultValue `'right'` на десктопе, `'top'` на мобильных устройствах
   */
  placement?: MUITooltipProps['placement'];

  /**
   * Дочерние элементы компонента InputTooltip.
   */
  children?: ReactNode;
}

/**
 * Компонент для отображения всплывающей подсказки (tooltip) рядом с полем ввода, обычно используется для предоставления дополнительной информации или помощи.
 *
 * @public
 * @param title - Заголовок всплывающей подсказки.
 * @param description - Основной текст описания всплывающей подсказки.
 * @param subText - Дополнительный текст подсказки (необязательный).
 * @param placement - Позиционирование всплывающей подсказки относительно целевого элемента.
 * @param children - Дочерние элементы компонента InputTooltip.
 * @returns  JSX-элемент, представляющий всплывающую подсказку для поля ввода.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link InputTooltipProps}
 */
export const InputTooltip = ({
  title,
  description,
  subText,
  placement,
  children = null,
}: InputTooltipProps) => {
  const isDesktop = useIsDesktop();

  if (!placement) {
    placement = isDesktop ? 'right' : 'top';
  }

  return (
    <Box
      height={20}
      className="aqa_input_tooltip"
      testId="input-tooltip-test-id"
    >
      <Tooltip
        placement={placement}
        title={
          <Box>
            {title && (
              <Box>
                <Typography variant="subtitle1" color="text.dark.primary">
                  {title}
                </Typography>
              </Box>
            )}
            {description && (
              <Box>
                <Typography variant="caption" color="text.dark.primary">
                  {description}
                </Typography>
              </Box>
            )}

            {!!children && <Box mt="8">{children}</Box>}

            {subText && (
              <Box mt="8">
                <Typography variant="caption" color="text.light.tertirary">
                  {subText}
                </Typography>
              </Box>
            )}
          </Box>
        }
      >
        <Box width="fit-content">
          <Icon
            name="help_outline"
            size="20"
            color="stroke.secondary"
            data-testid="help-icon-test-id"
          />
        </Box>
      </Tooltip>
    </Box>
  );
};
